import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../firebase/AuthContext';
import Navbar from '../components/Navbar';
import { 
  HiChartBar, 
  HiCheckCircle, 
  HiLockClosed,
  HiTrendingUp,
  HiUsers,
  HiArrowLeft
} from 'react-icons/hi';
import GoogleSignInButton from '../components/GoogleSignInButton';

const PollingZone = () => {
  const navigate = useNavigate();
  const { user, backendUserId, loading: authLoading } = useAuth();
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [votedPolls, setVotedPolls] = useState(new Set());
  const [userVotes, setUserVotes] = useState({});
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [votingOption, setVotingOption] = useState(null); // Track specific option being voted on

  const fetchPolls = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('https://izttvhkaqp.us-east-1.awsapprunner.com/api/polls/list', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });
      
      if (!response.ok) {
        console.error('API Error:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('Error details:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Polls API response:', data);
      
      if (data.success) {
        setPolls(data.polls || []);
      } else {
        setError(data.message || 'Failed to load polls');
      }
    } catch (err) {
      console.error('Error fetching polls:', err);
      setError('Failed to connect to polling service. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch user's votes
  const fetchUserVotes = async () => {
    if (!backendUserId) return;

    try {
      const response = await fetch(`https://izttvhkaqp.us-east-1.awsapprunner.com/api/polls/user/${backendUserId}/votes`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        console.error('Failed to fetch user votes:', response.status);
        return;
      }

      const data = await response.json();
      console.log('User votes response:', data);

      if (data.success && data.voted_polls) {
        setUserVotes(data.voted_polls);
        setVotedPolls(new Set(Object.keys(data.voted_polls)));
      }
    } catch (err) {
      console.error('Error fetching user votes:', err);
    }
  };

  // Fetch all polls on component mount and when user changes
  useEffect(() => {
    fetchPolls();
    if (backendUserId) {
      fetchUserVotes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backendUserId]);

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const handleVote = async (pollId, selectedOption) => {
    if (!user || !backendUserId) {
      setShowLoginModal(true);
      return;
    }

    // Immediate feedback for logged-in users - mark specific option as voting
    const votingKey = `${pollId}-${selectedOption}`;
    console.log('Setting voting option:', votingKey);
    setVotingOption(votingKey);

    try {
      const requestBody = {
        poll_id: pollId,
        selected_option: selectedOption,
        user_id: backendUserId
      };
      
      console.log('Voting with:', requestBody);
      
      const response = await fetch('https://izttvhkaqp.us-east-1.awsapprunner.com/api/polls/vote', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      console.log('Vote response:', data);

      if (!response.ok) {
        // Check if user already voted
        if (response.status === 400 && data.detail && data.detail.includes('already voted')) {
          // Extract the previous vote from error message
          const voteMatch = data.detail.match(/Your vote: (.+)$/);
          const previousVote = voteMatch ? voteMatch[1] : selectedOption;
          
          console.log('User already voted:', previousVote);
          
          // Update UI to show already voted state
          setVotedPolls(prev => new Set([...prev, pollId]));
          setUserVotes(prev => ({ ...prev, [pollId]: previousVote }));
          setVotingOption(null);
          return; // Exit gracefully
        }
        
        // Other errors
        console.error('Vote API Error:', response.status, data);
        alert(data.detail || data.message || 'Failed to record vote');
        setVotingOption(null);
        return;
      }
      
      if (data.success) {
        // Update the poll with new vote counts
        setPolls(prevPolls => 
          prevPolls.map(poll => {
            if (poll.poll_id === pollId) {
              return {
                ...poll,
                votes: data.poll.votes,
                total_votes: data.poll.total_votes
              };
            }
            return poll;
          })
        );
        
        // Mark this poll as voted and save user's selection
        setVotedPolls(prev => new Set([...prev, pollId]));
        setUserVotes(prev => ({ ...prev, [pollId]: selectedOption }));
      } else {
        alert(data.message || 'Failed to record vote');
      }
    } catch (err) {
      console.error('Error voting:', err);
      alert('Failed to submit vote. Please try again.');
    } finally {
      // Clear voting state
      console.log('Clearing voting option');
      setVotingOption(null);
    }
  };

  const calculatePercentage = (votes, total) => {
    if (!total || total === 0) return 0;
    return ((votes / total) * 100).toFixed(1);
  };

  const PollCard = ({ poll }) => {
    const hasVoted = votedPolls.has(poll.poll_id);
    const userSelection = userVotes[poll.poll_id];
    const options = poll.options || [];
    const totalVotes = poll.total_votes || 0;

    return (
      <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow p-6 border border-gray-200">
        {/* Poll Question */}
        <div className="mb-6">
          <h3 className="text-xl font-bold text-gray-900 mb-2">{poll.question}</h3>
          {poll.description && (
            <p className="text-sm text-gray-600">{poll.description}</p>
          )}
          <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <HiUsers className="text-base" />
              {totalVotes} {totalVotes === 1 ? 'vote' : 'votes'}
            </span>
            {poll.category && (
              <span className="px-2 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
                {poll.category}
              </span>
            )}
          </div>
        </div>

        {/* Options */}
        <div className="space-y-3">
          {options.map((option, index) => {
            const votes = poll.votes?.[option] || 0;
            const percentage = calculatePercentage(votes, totalVotes);
            const isSelected = userSelection === option;

            if (hasVoted) {
              // Show results after voting
              return (
                <div key={index} className="relative">
                  <div 
                    className={`relative overflow-hidden rounded-lg border-2 transition-all ${
                      isSelected 
                        ? 'border-primary bg-primary/5' 
                        : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    {/* Progress bar background */}
                    <div 
                      className="absolute inset-0 bg-primary/20 transition-all duration-500"
                      style={{ width: `${percentage}%` }}
                    />
                    
                    {/* Content */}
                    <div className="relative px-4 py-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {isSelected && <HiCheckCircle className="text-primary text-xl" />}
                        <span className={`font-medium ${isSelected ? 'text-primary' : 'text-gray-900'}`}>
                          {option}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-600">{votes}</span>
                        <span className="font-bold text-primary">{percentage}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            } else {
              // Show votable buttons before voting
              const isThisOptionVoting = votingOption === `${poll.poll_id}-${option}`;
              const isAnyOptionVoting = votingOption && votingOption.startsWith(`${poll.poll_id}-`);
              
              if (isThisOptionVoting) {
                console.log('Rendering loading state for:', option, 'votingOption:', votingOption);
              }
              
              return (
                <button
                  key={index}
                  onClick={() => handleVote(poll.poll_id, option)}
                  disabled={!user || !backendUserId || isAnyOptionVoting}
                  className={`w-full px-4 py-3 rounded-lg border-2 text-left font-medium transition-all ${
                    !user || !backendUserId
                      ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                      : isThisOptionVoting
                      ? 'border-primary bg-primary/10 text-primary cursor-wait'
                      : 'border-gray-300 bg-white text-gray-900 hover:border-primary hover:bg-primary/5 cursor-pointer active:scale-[0.98]'
                  }`}
                >
                  {isThisOptionVoting ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {option}
                    </span>
                  ) : (
                    option
                  )}
                </button>
              );
            }
          })}
        </div>

        {/* Voted indicator */}
        {hasVoted && userSelection && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-sm text-primary font-medium flex items-center gap-2">
              <HiCheckCircle className="text-base" />
              You voted for: {userSelection}
            </p>
          </div>
        )}
      </div>
    );
  };

  const LoginModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative">
        <button
          onClick={() => setShowLoginModal(false)}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        <div className="text-center">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <HiLockClosed className="text-primary text-3xl" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-3">
            Login Required
          </h3>
          <p className="text-gray-600 mb-6">
            Please log in to participate in polls and see how your opinions compare with the community.
          </p>
          <GoogleSignInButton variant="primary" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navbar />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Back Button */}
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-600 hover:text-primary transition-colors mb-8 group"
          >
            <HiArrowLeft className="text-xl group-hover:-translate-x-1 transition-transform" />
            <span className="font-medium">Back to Home</span>
          </button>

          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium mb-4">
              <HiTrendingUp className="text-base" />
              Live Polling
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
              Polling Zone
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Share your opinion on trending AI topics and see how the community thinks
            </p>
            
            {!user && (
              <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg max-w-lg mx-auto">
                <p className="text-sm text-yellow-800 flex items-center justify-center gap-2">
                  <HiLockClosed className="text-base" />
                  Sign up/Login to vote and track your opinions - takes just a few seconds
                </p>
              </div>
            )}
          </div>

          {/* Stats Bar */}
          {user && (
            <div className="bg-white rounded-xl shadow-sm p-6 mb-8 border border-primary/10">
              <div className="flex items-center justify-center gap-8 text-center">
                <div>
                  <div className="text-3xl font-bold text-primary">{votedPolls.size}</div>
                  <div className="text-sm text-gray-600">Polls Voted</div>
                </div>
                <div className="h-12 w-px bg-gray-300" />
                <div>
                  <div className="text-3xl font-bold text-primary">{polls.length}</div>
                  <div className="text-sm text-gray-600">Total Polls</div>
                </div>
              </div>
            </div>
          )}

          {/* Content */}
          <div>
            {/* Loading State */}
            {loading && (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                <p className="mt-4 text-gray-600">Loading polls...</p>
              </div>
            )}

            {/* Error State */}
            {error && !loading && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                <p className="text-red-800">{error}</p>
                <button
                  onClick={fetchPolls}
                  className="mt-4 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Retry
                </button>
              </div>
            )}

            {/* Polls Grid */}
            {!loading && !error && polls.length > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {polls.map((poll) => (
                  <PollCard key={poll.poll_id} poll={poll} />
                ))}
              </div>
            )}

            {/* Empty State */}
            {!loading && !error && polls.length === 0 && (
              <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-200">
                <HiChartBar className="text-6xl text-gray-300 mx-auto mb-4" />
                <p className="text-xl text-gray-600">No polls available at the moment</p>
                <p className="text-sm text-gray-500 mt-2">Check back soon for new polls!</p>
              </div>
            )}
          </div>
        </div>

      {/* Login Modal - For vote button clicks */}
      {showLoginModal && <LoginModal />}
    </div>
  );
};

export default PollingZone;
